import { Megaphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { StatusBadge, type StatusTone } from "../data-display/StatusBadge";

interface CampaignRow {
  campaign_id: string;
  campaign_name: string;
  channel: string;
  sent_at: string | null;
  status: string;
  error: string | null;
}

const TONES: Record<string, StatusTone> = {
  queued: "info",
  logged: "info",
  suppressed: "warning",
  failed: "danger",
  pending: "neutral",
};

const LABELS: Record<string, string> = {
  queued: "Sent",
  logged: "Sent",
  suppressed: "Blocked",
  failed: "Failed",
  pending: "Pending",
};

/**
 * Every campaign this contact has received.
 *
 * Lives on the contact record rather than in a marketing report because the
 * question, "what have we been sending this person?", is asked while looking
 * at them, usually right before calling them back.
 *
 * A consent-suppressed send is shown as "Blocked" with its reason rather than
 * omitted. Someone checking why a customer never heard from them needs to see
 * that the system deliberately withheld it; a silent gap looks like a bug and
 * invites a manual re-send that would violate the opt-out.
 */
export const ContactCampaignHistory = ({
  contactId,
  hideHeader,
}: {
  contactId: number;
  hideHeader?: boolean;
}) => {
  const { data, isPending } = useQuery<CampaignRow[]>({
    queryKey: ["contact-campaigns", contactId],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "report_contact_campaigns",
        { p_contact_id: contactId },
      );
      if (error) throw new Error(error.message);
      return (data ?? []) as CampaignRow[];
    },
  });

  const rows = data ?? [];
  // Nothing to say on a contact who has never been in a campaign, which is
  // most of them, so the section disappears rather than showing an empty box.
  if (!isPending && rows.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-col gap-2.5">
      {hideHeader ? null : (
        <div className="flex items-center gap-2">
          <Megaphone
            className="size-4 text-muted-foreground"
            strokeWidth={1.75}
          />
          <h3 className="text-[14px] font-semibold tracking-tight text-foreground">
            Campaigns
          </h3>
          <span className="text-[12px] text-muted-foreground">
            {rows.length}
          </span>
        </div>
      )}

      {isPending ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li
              key={row.campaign_id}
              className="flex min-w-0 items-start justify-between gap-2 border-b border-border-subtle pb-2 last:border-0 last:pb-0"
            >
              <div className="min-w-0">
                <p className="truncate text-[13px] font-medium text-foreground">
                  {row.campaign_name}
                </p>
                <p className="text-[11.5px] text-muted-foreground">
                  {row.channel.toUpperCase()}
                  {row.sent_at
                    ? ` · ${new Date(row.sent_at).toLocaleDateString()}`
                    : " · not sent yet"}
                  {row.error ? ` · ${row.error}` : ""}
                </p>
              </div>
              <StatusBadge
                label={LABELS[row.status] ?? row.status}
                tone={TONES[row.status] ?? "neutral"}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
